import type {
  ContratDTO,
  ContratProDTO,
  EmployeurDTO,
  FormationDTO,
  SalarieDTO,
  TutoratDTO,
} from '@/features/api/extranet/dto/Demande/CPRO'
import type { FinancementDTO } from '@/features/api/extranet/dto/Demande/shared/FinancementDTO'
import {
  addPreBuilder,
  targetPreBuilder,
  type Flag,
  type ModelValidateurResult,
  type Regles,
} from '@/features/domaine/moteur-regles'
import { LevelRegleEnum } from '@/features/domaine/types/opco/shared'
import type { ContextReglesCpro, ReglesCPRO } from './types'

import type { EtablissementAdherentDTO } from '@/features/api/extranet/dto/shared'
import {
  addMonth,
  checkDateInRange,
  getAge,
  getNbJour,
  getToday,
  isDate,
  isNotNullUndefinedOrEmptyString,
  isNullOrUndefined,
  isNumber,
} from '@/features/shared/tools'
import { PropertyStatus } from '../../property-status'
import { getDureeContratMois } from '../CAPP/tools'
import {
  checkNir,
  checkNirDateNaissance,
  checkNirDepartement,
  checkNirSexe,
  checkNirTemporaire,
  getRangeDate,
  isAdherentOuMandatGestion,
  preDateValide,
  preEstNirPortail,
  preNotNullOrEmpty,
  rgEstCodePostalFactory,
  rgEstCommuneFactory,
  rgEstDate,
  rgEstMailFactory,
  rgEstMaxLengthFactory,
  rgEstMinLengthFactory,
  rgEstNomOuPrenomFactory,
  rgEstNumeric,
  rgEstNumericMaxFactory,
  rgEstNumericMinFactory,
  rgEstNumTelFactory,
  rgEstObligatoireFactory,
  rgEstVoieFactory,
  rgEstVraiFactory,
  rgTestRegExpFactory,
} from '../shared'
import { SAISIE_INVALIDE, SAISIE_OBLIGATOIRE } from '../shared/messages-erreurs'
import { preMajeur, preMineur, preNirTemporaire, preNotNirTemporaire } from './preconditions'
import { getRangeDateConclusion } from './tools'
import { plainDateFromDateOnly } from '@/features/shared/tools/dateOnly.ts'

/* Initalisation des helpers */
const targetPre = targetPreBuilder<ContratProDTO, ContextReglesCpro>()
const addPre = addPreBuilder<ContratProDTO, ContextReglesCpro>()

/* Declaration de type pour les sous modeles */
type RegleCPROMembre<T extends object> = Regles<T, ContextReglesCpro, LevelRegleEnum, Flag, ContratProDTO>

type ReglesCPROEmployeur = RegleCPROMembre<EmployeurDTO>
type ReglesCPROEmployeurEtablissement = RegleCPROMembre<EtablissementAdherentDTO>
type ReglesCPROSalarie = RegleCPROMembre<SalarieDTO>
type ReglesCPROTutorat = RegleCPROMembre<TutoratDTO>
type ReglesCPROContrat = RegleCPROMembre<ContratDTO>
type ReglesCPROFormation = RegleCPROMembre<FormationDTO>
type ReglesCPROFinancement = RegleCPROMembre<FinancementDTO>

/* Employeur */
/* Etablissement */
const reglesEtablissementEmployeur: ReglesCPROEmployeurEtablissement = {
  /* en lecture seule */
  adresse: {},
  denomination: {},
  siret: {},
  typeEntreprise: {
    ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
  },
  idcc: {
    code: {
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.employeur?.etablissement?.idcc?.code?.$status === PropertyStatus.Required,
        rgEstObligatoireFactory(LevelRegleEnum.Technique),
      ),
    },
  },
  naf: {
    code: {
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.employeur?.etablissement?.naf?.code?.$status === PropertyStatus.Required,
        rgEstObligatoireFactory(LevelRegleEnum.Technique),
      ),
    },
  },
}

const reglesEmployeur: ReglesCPROEmployeur = {
  contact: {
    email: {
      ...rgEstMaxLengthFactory(200),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.employeur?.contact?.email?.$status === PropertyStatus.Required,
        rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      ),
      ...rgEstMailFactory(LevelRegleEnum.Metier),
    },
    telephone: {
      ...rgEstMaxLengthFactory(10),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.employeur?.contact?.telephone?.$status === PropertyStatus.Required,
        rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      ),
      ...rgEstNumTelFactory(LevelRegleEnum.Metier),
    },
  },

  caisseRetraitreComplementaire: {
    ...rgEstMaxLengthFactory(100),
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.employeur?.caisseRetraitreComplementaire?.$status === PropertyStatus.Required,
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
  },
  organismePrevoyance: {
    ...rgEstMaxLengthFactory(100),
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.employeur?.organismePrevoyance?.$status === PropertyStatus.Required,
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
  },
  effectifTotalEntreprise: {
    ...rgEstNumeric,
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.employeur?.effectifTotalEntreprise?.$status === PropertyStatus.Required,
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
    ...rgEstNumericMinFactory(0, LevelRegleEnum.Technique),
    ...rgEstNumericMaxFactory(1000000, LevelRegleEnum.Technique),
  },
  numeroProjet: {
    ...addPre(
      (_value, _parent, _model, context) => context.status.employeur?.numeroProjet?.$status === PropertyStatus.Required,
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
  },
  etablissement: reglesEtablissementEmployeur,
}

/* Salarie */
const reglesSalarie: ReglesCPROSalarie = {
  nomNaissance: {
    ...rgEstMaxLengthFactory(50),
    ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ...rgEstNomOuPrenomFactory(LevelRegleEnum.Metier),
  },
  prenom: {
    ...rgEstMaxLengthFactory(50),
    ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ...rgEstNomOuPrenomFactory(LevelRegleEnum.Metier),
  },
  nom: {
    ...rgEstMaxLengthFactory(50),
    ...rgEstNomOuPrenomFactory(LevelRegleEnum.Metier),
  },
  nirTemporaire: {
    ...addPre(
      (_value, _parent, _model, context) => context.status.salarie?.nirTemporaire?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  nir: {
    ...addPre(preNotNirTemporaire, rgEstMaxLengthFactory(13, LevelRegleEnum.Metier)),
    ...rgEstMaxLengthFactory(250),
    ...addPre((_value, _parent, _model, context) => context.status.salarie?.nir?.$status === PropertyStatus.Required, {
      ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    }),

    'check-nir': {
      pre: [preNotNullOrEmpty, preEstNirPortail, preNotNirTemporaire],
      validateur: (value) => checkNir(value!),
      message: 'Le format du NIR est incorrect',
      level: LevelRegleEnum.Metier,
    },
    /*
    'check-nir-api': {
      pre: [preNotNullOrEmpty, preEstNirPortail, preNotNirTemporaire, (_, parent) => (parent.nomNaissance?.length ?? 0) > 2],
      validateur: async (value, parent, _modele, context) => {
        console.log(value)
        if (['1', '2'].includes(value![0]) === false) return

        const result = await context.services.validerNir(value!, parent.nomNaissance!)
        return result.isValidFormat === true && result.nirDetails !== null
      },
      message: 'Le NIR ne correspond pas au nom de naissance',
      level: LevelRegleEnum.Metier,
    },
    */
    'check-nir-temporaire': {
      pre: [preNotNullOrEmpty, preEstNirPortail, preNirTemporaire],
      validateur: (value) => checkNirTemporaire(value!),
      message: 'Le format du NIR temporaire est incorrect',
      level: LevelRegleEnum.Metier,
    },
  },
  sexe: {
    ...addPre((_value, _parent, _model, context) => context.status.salarie?.sexe?.$status === PropertyStatus.Required, {
      ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    }),
    controleSexeNir: {
      pre: [preNotNullOrEmpty, targetPre('salarie.nir', preNotNullOrEmpty), preNotNirTemporaire],
      validateur: (value, parent) => checkNirSexe(parent?.nir ?? '', value!),
      message: 'Le sexe du salarié ne correspond pas au NIR saisi',
      level: LevelRegleEnum.Metier,
    },
  },
  dateNaissance: {
    ...rgEstDate,
    ...addPre(
      (_value, _parent, _model, context) => context.status.salarie?.dateNaissance?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    controleDateNaissanceNir: {
      pre: [preNotNullOrEmpty, targetPre('salarie.nir', preNotNullOrEmpty), preNotNirTemporaire],
      validateur: (value, parent) => checkNirDateNaissance(parent?.nir ?? '', new Date(value!)),
      message: 'La date de naissance du salarié ne correspond pas au NIR saisi',
      level: LevelRegleEnum.Metier,
    },
  },
  departementNaissance: {
    ...rgEstMaxLengthFactory(3),
    ...rgTestRegExpFactory(/^((\d{2})|(971|972|973|974|975|976|978|986|987|988)|(2[abAB]))$/),
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.salarie?.departementNaissance?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    controleDepartementNir: {
      pre: [preNotNullOrEmpty, targetPre('salarie.nir', preNotNullOrEmpty), preNotNirTemporaire],
      validateur: (value, parent) => checkNirDepartement(parent?.nir ?? '', value!),
      message: 'Le département de naissance du salarié ne correspond pas au NIR saisi',
      level: LevelRegleEnum.Metier,
    },
  },
  communeNaissance: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.salarie?.communeNaissance?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    ...rgEstMaxLengthFactory(50),
    ...rgEstCommuneFactory(LevelRegleEnum.Metier),
  },
  beneficieReconnaissanceTravailleurHandicape: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.salarie?.beneficieReconnaissanceTravailleurHandicape?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  equivalenceJeune: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.salarie?.equivalenceJeune?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  extensionBOE: {
    ...addPre(
      (_value, _parent, _model, context) => context.status.salarie?.extensionBOE?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  csp: {
    ...addPre((_value, _parent, _model, context) => context.status.salarie?.csp?.$status === PropertyStatus.Required, {
      ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    }),
  },
  inscritPoleEmploi: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.salarie?.inscritPoleEmploi?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  numeroInscriptionPoleEmploi: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.salarie?.numeroInscriptionPoleEmploi?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.salarie?.numeroInscriptionPoleEmploi?.$status !== PropertyStatus.NotPresent,
      {
        ...rgEstMinLengthFactory(8, LevelRegleEnum.Metier),
        ...rgEstMaxLengthFactory(12, LevelRegleEnum.Technique),
      },
    ),
  },
  dureeMoisPoleEmploi: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.salarie?.dureeMoisPoleEmploi?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.salarie?.dureeMoisPoleEmploi?.$status !== PropertyStatus.NotPresent,
      {
        ...rgEstNumericMinFactory(0, LevelRegleEnum.Technique),
        ...rgEstNumericMaxFactory(5000, LevelRegleEnum.Technique),
      },
    ),
  },
  situationAvantContrat: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.salarie?.situationAvantContrat?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  typeMinimuSocial: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.salarie?.typeMinimuSocial?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  diplomeOuTitreLePlusEleveObtenu: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.salarie?.diplomeOuTitreLePlusEleveObtenu?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  adresse: {
    voie: {
      ...rgEstMaxLengthFactory(38),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.salarie?.adresse?.voie?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
      ...rgEstVoieFactory(LevelRegleEnum.Metier),
    },
    complement: {
      ...rgEstMaxLengthFactory(38),
    },
    codePostal: {
      ...rgEstMaxLengthFactory(10),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.salarie?.adresse?.codePostal?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
      ...rgEstCodePostalFactory(LevelRegleEnum.Metier),
    },
    commune: {
      ...rgEstMaxLengthFactory(50),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.salarie?.adresse?.commune?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
      ...rgEstCommuneFactory(LevelRegleEnum.Metier),
    },
  },
  email: {
    ...rgEstMaxLengthFactory(64),
    ...rgEstMailFactory(LevelRegleEnum.Metier),
    ...addPre(
      (_value, _parent, _model, context) => context.status.salarie?.email?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  telephone: {
    ...rgEstMaxLengthFactory(10),
    ...rgEstNumTelFactory(LevelRegleEnum.Metier),
    ...addPre(
      (_value, _parent, _model, context) => context.status.salarie?.telephone?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
}

/* Tutorat */
const reglesTutorat: ReglesCPROTutorat = {
  attestationTuteurCriteresEligibilite: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.tutorat?.attestationTuteurCriteresEligibilite?.$status === PropertyStatus.Required,
      {
        ...rgEstVraiFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  attestationTuteurTroisStagiaires: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.tutorat?.attestationTuteurTroisStagiaires?.$status === PropertyStatus.Required,
      {
        ...rgEstVraiFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  tuteurEntrepriseEmployeur: {
    souhaiteAideFonctionTutorale: {
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.tutorat?.tuteurEntrepriseEmployeur?.souhaiteAideFonctionTutorale?.$status ===
          PropertyStatus.Required,
        rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      ),
    },
    nom: {
      ...rgEstMaxLengthFactory(50),
      ...rgEstNomOuPrenomFactory(LevelRegleEnum.Metier),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.tutorat?.tuteurEntrepriseEmployeur?.nom?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
    },
    prenom: {
      ...rgEstMaxLengthFactory(50),
      ...rgEstNomOuPrenomFactory(LevelRegleEnum.Metier),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.tutorat?.tuteurEntrepriseEmployeur?.prenom?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
    },
    dateNaissance: {
      ...rgEstDate,
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.tutorat?.tuteurEntrepriseEmployeur?.dateNaissance?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
      controle18Ans: {
        pre: [preNotNullOrEmpty],
        validateur: (value, _parent, _modele) => {
          const age = getAge(new Date(value!), getToday())
          return age >= 18
        },
        message: 'Le tuteur doit être majeur',
        level: LevelRegleEnum.Metier,
      },
      controleMaximum: {
        pre: [preNotNullOrEmpty],
        validateur: (value, _parent, _modele) => {
          const age = getAge(new Date(value!), getToday())
          return age <= 100
        },
        message: "L'âge du tuteur semble incorrect",
        level: LevelRegleEnum.Metier,
      },
    },

    emploiOccupe: {
      ...rgEstMaxLengthFactory(100),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.tutorat?.tuteurEntrepriseEmployeur?.emploiOccupe?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
    },
  },
  tuteurEntrepriseUtilisatrice: {
    souhaiteAideFonctionTutorale: {
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.tutorat?.tuteurEntrepriseUtilisatrice?.souhaiteAideFonctionTutorale?.$status ===
          PropertyStatus.Required,
        rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      ),
    },
    nom: {
      ...rgEstMaxLengthFactory(50),
      ...rgEstNomOuPrenomFactory(LevelRegleEnum.Metier),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.tutorat?.tuteurEntrepriseUtilisatrice?.nom?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
    },
    prenom: {
      ...rgEstMaxLengthFactory(50),
      ...rgEstNomOuPrenomFactory(LevelRegleEnum.Metier),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.tutorat?.tuteurEntrepriseUtilisatrice?.prenom?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
    },
    dateNaissance: {
      ...rgEstDate,
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.tutorat?.tuteurEntrepriseUtilisatrice?.dateNaissance?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
      controle18Ans: {
        pre: [preNotNullOrEmpty],
        validateur: (value, _parent, _modele) => {
          const age = getAge(new Date(value!), getToday())
          return age >= 18
        },
        message: 'Le tuteur doit être majeur',
        level: LevelRegleEnum.Metier,
      },
      controleMaximum: {
        pre: [preNotNullOrEmpty],
        validateur: (value, _parent, _modele) => {
          const age = getAge(new Date(value!), getToday())
          return age <= 100
        },
        message: "L'âge du tuteur semble incorrect",
        level: LevelRegleEnum.Metier,
      },
    },

    emploiOccupe: {
      ...rgEstMaxLengthFactory(100),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.tutorat?.tuteurEntrepriseUtilisatrice?.emploiOccupe?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
    },
  },
}

/* Contrat */
const reglesContrat: ReglesCPROContrat = {
  natureContrat: {
    ...addPre(
      (_value, _parent, _model, context) => context.status.contrat?.natureContrat?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  typeContratAvenant: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.contrat?.typeContratAvenant?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  emploiOccupe: {
    ...addPre(
      (_value, _parent, _model, context) => context.status.contrat?.emploiOccupe?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  emploinCcn: {
    ...addPre(
      (_value, _parent, _model, context) => context.status.contrat?.emploinCcn?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  niveauQualificationCcn: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.contrat?.niveauQualificationCcn?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    ...rgEstMaxLengthFactory(15, LevelRegleEnum.Metier),
  },
  coefficientCcn: {
    ...addPre(
      (_value, _parent, _model, context) => context.status.contrat?.coefficientCcn?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  dureePeriodeEssaiJours: {
    ...rgEstNumeric,
    ...addPre(
      (_value, _parent, _model, context) => context.status.contrat?.coefficientCcn?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  dateDebutExecutionContrat: {
    ...rgEstDate,
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.contrat?.dateDebutExecutionContrat?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    controleRange: {
      pre: [preNotNullOrEmpty, targetPre('contrat.dateConclusion', preDateValide)],
      validateur: (value, _parent, modele) =>
        checkDateInRange(new Date(value!), getRangeDate(new Date(modele.contrat!.dateConclusion!), null)),
      message:
        "La date de début d'exécution du contrat doit être postérieure ou égale à la date de conclusion (signature du contrat)",
      level: LevelRegleEnum.Metier,
    },
    controleDateFinContrat: {
      pre: [preNotNullOrEmpty, targetPre('contrat.dateFinCddOuActionSiCdi', preDateValide)],
      validateur: (value, _parent, modele) => {
        const dateMax = new Date(modele.contrat!.dateFinCddOuActionSiCdi!)
        dateMax.setDate(dateMax.getDate() - 1)
        return checkDateInRange(new Date(value!), getRangeDate(null, dateMax))
      },
      message:
        "La date de début d'exécution du contrat ne peut pas être supérieure ou égale à la date de fin du CDD ou de l'action de professionnalisation si CDI",
      level: LevelRegleEnum.Metier,
    },
  },
  dateEffetAvenant: {
    ...rgEstDate,
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.contrat?.dateEffetAvenant?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    controleRange: {
      pre: [preNotNullOrEmpty, targetPre('contrat.dateConclusion', preDateValide)],
      validateur: (value, _parent, modele) =>
        checkDateInRange(new Date(value!), getRangeDate(new Date(modele.contrat!.dateConclusion!), null)),
      message: "La date d'effet de l'avenant doit être supérieure à la date de conclusion du contrat",
      level: LevelRegleEnum.Metier,
    },
  },
  dateFinCddOuActionSiCdi: {
    ...rgEstDate,
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.contrat?.dateFinCddOuActionSiCdi?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    controleRange: {
      pre: [preNotNullOrEmpty, targetPre('contrat.dateDebutExecutionContrat', preDateValide)],
      validateur: (value, _parent, modele) =>
        checkDateInRange(new Date(value!), getRangeDate(new Date(modele.contrat!.dateDebutExecutionContrat!), null)),
      message: 'La date de fin doit être supérieure à la date de début',
      level: LevelRegleEnum.Metier,
    },
    controleDureeMinimum: {
      pre: [preNotNullOrEmpty, targetPre('contrat.dateDebutExecutionContrat', preDateValide)],
      validateur: (value, parent, _modele) => {
        // if (!isDate(parent.dateDebutExecutionContrat)) return true // inutile
        const dateDebut = plainDateFromDateOnly(parent.dateDebutExecutionContrat)!
        const dateFin = plainDateFromDateOnly(value)!.add({ days: 1 })
        const duree = dateDebut.until(dateFin, { largestUnit: 'months' })
        return duree.months >= 6
      },
      message: "La durée minimum d'un contrat est de 6 mois",
      level: LevelRegleEnum.Metier,
    },
  },
  dateConclusion: {
    ...rgEstDate,
    ...addPre(
      (_value, _parent, _model, context) => context.status.contrat?.dateConclusion?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    controleRange: {
      pre: [preNotNullOrEmpty],
      validateur: (value) => checkDateInRange(new Date(value!), getRangeDateConclusion()),
      message: 'Le dossier ne peut être soumis qu’à partir de la date de conclusion (signature du contrat).',
      level: LevelRegleEnum.Metier,
    },
  },
  salaire: {
    modeRemuneration: {
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.contrat?.salaire?.modeRemuneration?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
    },
    montant: {
      ...rgEstNumeric,
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.contrat?.salaire?.montant?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
      ...rgEstNumericMinFactory(0, LevelRegleEnum.Technique),
      ...rgEstNumericMaxFactory(10000, LevelRegleEnum.Technique),
    },
  },
  dureeHebdomadaire: {
    heure: {
      ...rgEstNumeric,
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.contrat?.dureeHebdomadaire?.heure?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
      format: {
        pre: [preNotNullOrEmpty],
        validateur: (_value, parent) => {
          return !isNullOrUndefined(parent.minute)
        },
        message: SAISIE_INVALIDE,
        level: LevelRegleEnum.Technique,
      },
      controleMoins18Ans: {
        pre: [preNotNullOrEmpty, preMineur],
        validateur: (value, parent) => {
          const duree: number = Number(value) + (parent.minute ?? 0.0) / 100
          return duree <= 35
        },
        message: 'Avant 18 ans, la durée hebdomadaire de travail ne peut excéder 35 h',
        level: LevelRegleEnum.Metier,
      },
      controlePlus18Ans: {
        pre: [preNotNullOrEmpty, preMajeur],
        validateur: (value, parent) => {
          const duree: number = Number(value) + (parent.minute ?? 0.0) / 100
          return duree <= 48
        },
        message: 'Après 18 ans, la durée hebdomadaire de travail ne peut excéder 48 h',
        level: LevelRegleEnum.Metier,
      },
    },
  },
}

/* Financement */
const reglesFinancement: ReglesCPROFinancement = {
  attestationResteChargeContribution: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.financement?.attestationResteChargeContribution?.$status === PropertyStatus.Required,
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
  },
  modePriseEnChargeReste: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.financement?.modePriseEnChargeReste?.$status === PropertyStatus.Required,
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
  },
}

/* Formation */
const reglesFormation: ReglesCPROFormation = {
  diplomeOuTitreVise: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.formation?.diplomeOuTitreVise?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  typeQualification: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.formation?.typeQualification?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },

  referenceFormationInterneEntreprise: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.formation?.referenceFormationInterneEntreprise?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    ...rgEstMaxLengthFactory(30),
  },
  referenceProjet: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.formation?.referenceProjet?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    ...rgEstMaxLengthFactory(30),
  },
  dureeTotale: {
    ...rgEstNumeric,
    ...addPre(
      (_value, _parent, _model, context) => context.status.formation?.dureeTotale?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    ...rgEstNumericMinFactory(
      149,
      LevelRegleEnum.Technique,
      'La durée de formation doit être supérieure ou égale à 150 heures',
    ),
    controleQuotite: {
      pre: [
        preNotNullOrEmpty,
        targetPre('contrat.dateDebutExecutionContrat', preDateValide),
        targetPre('contrat.dateFinCddOuActionSiCdi', preDateValide),
        targetPre('contrat.dureeHebdomadaire.heure', preNotNullOrEmpty),
      ],
      validateur: (value, _parent, model) => {
        const nbJour =
          getNbJour(
            new Date(model.contrat!.dateDebutExecutionContrat!),
            new Date(model.contrat!.dateFinCddOuActionSiCdi!),
          ) + 1
        const duree: number =
          Number(model.contrat?.dureeHebdomadaire?.heure) + (model.contrat?.dureeHebdomadaire?.minute ?? 0.0) / 100
        const quotite = (value! / ((((1607 * nbJour) / 365) * duree) / 35)) * 100
        return quotite >= 15 && quotite <= 55
      },
      message: 'La durée de formation doit être comprise en 15 et 55 % de la durée totale du contrat.',
      level: LevelRegleEnum.Metier,
    },
  },
  dureeTotaleEnseignement: {
    ...rgEstNumeric,
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.formation?.dureeTotaleEnseignement?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    ...rgEstNumericMinFactory(-1, LevelRegleEnum.Technique, 'La durée doit être supérieure à 0 heure'),
    controleMaximum: {
      pre: [preNotNullOrEmpty],
      validateur: (value, parent, _model) => {
        if (isNullOrUndefined(parent.dureeTotale)) return true

        const duree: number = Number(parent.dureeTotale)

        return Number(value) <= duree
      },
      message: 'La durée de ces enseignements ne peut pas être supérieure à la durée totale des actions.',
      level: LevelRegleEnum.Metier,
    },
  },
  intitule: {
    ...addPre(
      (_value, _parent, _model, context) => context.status.formation?.intitule?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  modules: {
    dateDebut: {
      ...rgEstDate,
      controleDateFinFormation: {
        pre: [preNotNullOrEmpty],
        validateur: (value, parent, _modele) => {
          if (!isDate(parent.dateFin)) return true

          return checkDateInRange(new Date(value!), getRangeDate(null, new Date(parent.dateFin!)))
        },
        message: 'La date de début est supérieure à la date de fin',
        level: LevelRegleEnum.Metier,
      },
      controleDateDebutContrat: {
        pre: [
          preNotNullOrEmpty,
          targetPre('contrat.dateDebutExecutionContrat', preDateValide),
          targetPre('contrat.dateFinCddOuActionSiCdi', preDateValide),
        ],
        validateur: (value, _parent, model) => {
          const dateDebutFormation: Date = new Date(value!)
          const dateDebutContrat: Date = new Date(model.contrat!.dateDebutExecutionContrat!)
          const dateFinContrat: Date = new Date(model.contrat!.dateFinCddOuActionSiCdi!)
          return checkDateInRange(dateDebutFormation, getRangeDate(dateDebutContrat, dateFinContrat))
        },
        message: 'La date de début de formation doit avoir lieu pendant la durée du contrat',
        level: LevelRegleEnum.Metier,
      },
    },
    dateFin: {
      ...rgEstDate,
      /*
      controleDateDebutFormation: {
        pre: [preNotNullOrEmpty, targetPre('contrat.dateFinCddOuActionSiCdi', preDateValide)],
        validateur: (value, parent, model) => {
          if (!isDate(parent.dateDebut)) return true

          return checkDateInRange(
            new Date(value!),
            getRangeDate(null, new Date(model.contrat?.dateFinCddOuActionSiCdi!)),
          )
        },
        message: 'La date de fin de formation doit être durant la période de contrat',
        level: LevelRegleEnum.Metier,
      },
      */
      controleDateFinFormation: {
        pre: [preNotNullOrEmpty],
        validateur: (value, parent, _modele) => {
          if (!isDate(parent.dateDebut)) return true

          return checkDateInRange(new Date(value!), getRangeDate(new Date(parent.dateFin!), null))
        },
        message: 'La date de fin est inférieure à la date de début de formation',
        level: LevelRegleEnum.Metier,
      },
    },
    duree: {
      ...rgEstNumeric,
    },
    dureeFoad: {
      ...rgEstNumeric,
      controleMaximum: {
        pre: [preNotNullOrEmpty],
        validateur: (value, parent, _modele) => {
          if (isNumber(parent.duree) || parent.modalitePedagogique !== 'Mixte') return true
          const duree = Number(parent.duree)
          const dureeFoad = Number(value)
          return duree > dureeFoad
        },
        message: 'la durée FOAD ne peut pas être égale ou supérieure à la durée totale de la formation.',
        level: LevelRegleEnum.Metier,
      },
    },
    fraisAnnexesSalarie: {
      quantite: {
        ...rgEstNumeric,
        ...rgEstNumericMinFactory(0, LevelRegleEnum.Technique, 'La quantité doit être supérieure à 0'),
        ...rgEstNumericMaxFactory(1000, LevelRegleEnum.Technique, 'La quantité doit être inférieure à 1000'),
      },
      montant: {
        ...rgEstNumeric,
        ...rgEstNumericMinFactory(0, LevelRegleEnum.Technique, 'Le montant doit être supérieur à 0'),
        ...rgEstNumericMaxFactory(100000, LevelRegleEnum.Technique, 'Le montant doit être inférieur à 100000'),
      },
    },
    fraisPedagogiques: {
      montant: {
        ...rgEstNumeric,
        ...rgEstNumericMinFactory(0, LevelRegleEnum.Technique, 'Le montant doit être supérieur à 0'),
        ...rgEstNumericMaxFactory(100000, LevelRegleEnum.Technique, 'Le montant doit être inférieur à 100000'),
      },
    },
  },
}

/* REGLES  ------------------------------------------------------------------------ */
export const reglesConfiguration: ReglesCPRO = {
  sousDispositif: {
    ...addPre(
      (_value, _parent, _model, context) => context.status.sousDispositif?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  validationNotice: {
    ...rgEstVraiFactory(LevelRegleEnum.Transmettre),
  },
  attestationSurHonneur: {
    ...rgEstVraiFactory(LevelRegleEnum.Transmettre),
  },
  attestationPiecesJustificatives: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.attestationPiecesJustificatives?.$status === PropertyStatus.Required,
      {
        ...rgEstVraiFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  acceptationCg: {
    ...rgEstVraiFactory(LevelRegleEnum.Transmettre),
  },
  employeur: reglesEmployeur,
  salarie: reglesSalarie,
  tutorat: reglesTutorat,
  contrat: reglesContrat,
  formation: reglesFormation,
  financement: reglesFinancement,
  lieuSignature: {
    ...addPre((_value, _parent, _model, context) => context.status.lieuSignature?.$status === PropertyStatus.Required, {
      ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    }),
  },
  $model: {
    controle15ansDateConclusion: {
      pre: (model) => {
        return preDateValide(model.contrat?.dateConclusion) && preDateValide(model.salarie?.dateNaissance)
      },
      validateur: (model) => {
        const age = getAge(new Date(model.salarie!.dateNaissance!), new Date(model.contrat!.dateConclusion!))
        if (age < 16)
          return {
            'contrat.dateConclusion': false,
            'salarie.dateNaissance': false,
          }
      },
      message: "L'age du salarié doit être supérieur ou égal à 16 ans à la date de fin de conclusion du contrat",
      level: LevelRegleEnum.Metier,
    },

    /*
    controleAgeMaximum: {
      pre: (model) => {
        return preDateValide(model.contrat?.dateConclusion) && preDateValide(model.salarie?.dateNaissance)
      },
      validateur: (model) => {
        const dateConclusion = new Date(model.contrat?.dateConclusion!)
        const dateNaissance = new Date(model.salarie?.dateNaissance!)
        const dateNaissanceAnnee = new Date(model.salarie?.dateNaissance!)
        dateNaissanceAnnee.setFullYear(dateConclusion.getFullYear())

        const age = getAge(dateNaissance, dateConclusion)
        //const nbMois = getDureeContratMois(dateNaissanceAnnee, dateConclusion)

        if (
          age >= 26 &&
          isNullOrUndefined(model.salarie?.typeMinimuSocial) === true &&
          model.salarie?.inscritPoleEmploi === false
        ) {
          return {
            'contrat.dateConclusion': false,
            'salarie.dateNaissance': false,
          }
        }
      },
      message:
        "L'age du salarié doit être inferieur ou égal à 25 ans à la date de fin de conclusion du contrat pour les stagiaires non inscrit à France Travail ou si Type de minimum social renseigné ",
      level: LevelRegleEnum.Metier,
    },
    */
    controleDureePeriodeEssai: {
      pre: (model) => {
        return (
          preDateValide(model.contrat?.dateDebutExecutionContrat) &&
          preDateValide(model.contrat?.dateFinCddOuActionSiCdi) &&
          preNotNullOrEmpty(model.contrat?.natureContrat) &&
          preNotNullOrEmpty(model?.contrat?.dureePeriodeEssaiJours)
        )
      },
      validateur: (model, _parent, _context) => {
        const result: ModelValidateurResult<ContratProDTO> = {}

        const dateDebutExecutionContrat = new Date(model.contrat!.dateDebutExecutionContrat!)
        const dateFinCddOuActionSiCdi = new Date(model.contrat!.dateFinCddOuActionSiCdi!)
        const dureeMois = getDureeContratMois(dateDebutExecutionContrat, dateFinCddOuActionSiCdi)
        const dureePeriode = Number(model.contrat?.dureePeriodeEssaiJours)

        const nbJour = dateDebutExecutionContrat.getDate() - dateFinCddOuActionSiCdi.getDate()

        let dureeMax = 0
        // const dureeMin = 0

        // CAS CDD
        if (model.contrat?.natureContrat === 'Cdd') {
          if ((dureeMois < 6 || (dureeMois === 6 && nbJour === 0)) && dureePeriode > 15) {
            dureeMax = 15
          }
          if ((dureeMois > 6 || (dureeMois === 6 && nbJour !== 0)) && dureePeriode > 30) {
            dureeMax = 30
          }
        } else {
          // CAS CDI

          if (dureePeriode > 120) {
            dureeMax = 120
          }

          /*
          if (dureePeriode < 60 && dureePeriode !== 0) {
            dureeMin = 60
          }*/
        }

        if (dureeMax !== 0)
          result['contrat.dureePeriodeEssaiJours'] = `La durée maximum de la période d'essai est de ${dureeMax} jours.`

        /*
        if (dureeMin !== 0)
          result['contrat.dureePeriodeEssaiJours'] =
            `La durée minimum de la période d'essai est de ${dureeMin} jours. Si aucune période d’essai est prévue, veuillez saisir 0.`
        */
        return result
      },
      level: LevelRegleEnum.Metier,
    },
    controleDateContratEtFormation: {
      pre: (model) => {
        return (
          preDateValide(model.contrat?.dateDebutExecutionContrat) ||
          preDateValide(model.contrat?.dateFinCddOuActionSiCdi)
        )
      },
      validateur: (model) => {
        // const isDateDebutContrat = isDate(model.contrat!.dateDebutExecutionContrat)
        // const isDateFinContrat = isDate(model.contrat!.dateFinCddOuActionSiCdi)

        const dateDebutContrat = new Date(model.contrat!.dateDebutExecutionContrat!)
        const dateFinContrat = new Date(model.contrat!.dateFinCddOuActionSiCdi!)

        // On récupère la date début max
        const dateDebutFormationMin: string | null | undefined = model.formation?.modules
          ?.filter((x) => isDate(x.dateDebut) == true)
          ?.map((x) => x.dateDebut)
          .reduce((prev, curr) => (prev != null && new Date(prev!) < new Date(curr!) ? prev : curr), null)

        // On récupère la date fin max
        const dateFinFormationMax: string | null | undefined = model.formation?.modules
          ?.filter((x) => isDate(x.dateFin) == true)
          ?.map((x) => x.dateFin)
          .reduce((prev, curr) => (prev != null && new Date(prev!) > new Date(curr!) ? prev : curr), null)

        const result: ModelValidateurResult<ContratProDTO> = {}

        for (const [index, prop] of model.formation!.modules!.entries()) {
          // Contrôle date de début
          if (isDate(prop.dateDebut)) {
            const dateDebutFormation: Date = new Date(prop.dateDebut!)
            const dateDebutContratMax: Date = addMonth(dateDebutContrat, 2)

            if (
              checkDateInRange(dateDebutFormation, getRangeDate(null, dateDebutContratMax)) === false &&
              prop.dateDebut === dateDebutFormationMin
            ) {
              const message = 'La date de début de contrat doit être maximum 2 mois avant la date de début de formation'
              result[`formation.modules.${index}.dateDebut`] = message
              result['contrat.dateDebutExecutionContrat'] = message
            }
          }

          // Contrôle date de fin
          if (isDate(prop.dateFin)) {
            const dateFinFormation: Date = new Date(prop.dateFin!)
            const dateFinContratMin: Date = addMonth(dateFinContrat, -2)

            if (checkDateInRange(dateFinFormation, getRangeDate(null, dateFinContrat)) === false) {
              const message = 'La date de fin de formation doit être inférieure ou égale à la date de fin de contrat.'
              result[`formation.modules.${index}.dateFin`] = message
              result['contrat.dateFinCddOuActionSiCdi'] = message
            } else if (
              checkDateInRange(dateFinFormation, getRangeDate(dateFinContratMin, null)) === false &&
              prop.dateFin === dateFinFormationMax
            ) {
              const message = 'La date de fin de formation doit être au maximum 2 mois avant la date de fin de contrat'
              result[`formation.modules.${index}.dateFin`] = message
              result['contrat.dateFinCddOuActionSiCdi'] = message
            }
          }
        }

        return result
      },
      level: LevelRegleEnum.Metier,
    },

    controleObligatoireFormation: {
      validateur: (model, _gModel, context) => {
        const result: ModelValidateurResult<ContratProDTO> = {}
        if (
          context.status.formation?.nsf?.code?.$status === PropertyStatus.Required &&
          isNullOrUndefined(model.formation?.nsf)
        ) {
          result[`formation.nsf.code`] = false
        }
        return result
      },
      message: SAISIE_OBLIGATOIRE,
      level: LevelRegleEnum.Transmettre,
    },

    controleObligatoireModules: {
      validateur: (model, _gModel, context) => {
        const result: ModelValidateurResult<ContratProDTO> = {}

        for (const [index, prop] of model.formation!.modules!.entries()) {
          if (
            context.status.formation?.modules?.at(index)?.etablissementPrestataire?.siret?.valeur?.$status ===
              PropertyStatus.Required &&
            prop.typeModule === 'Externe' &&
            isNullOrUndefined(prop.etablissementPrestataire)
          ) {
            result[`formation.modules.${index}.etablissementPrestataire.siret.valeur`] = SAISIE_OBLIGATOIRE
          }

          if (
            context.status.formation?.modules?.at(index)?.modalitePedagogique?.$status === PropertyStatus.Required &&
            isNullOrUndefined(prop.modalitePedagogique)
          ) {
            result[`formation.modules.${index}.modalitePedagogique`] = SAISIE_OBLIGATOIRE
          }

          if (
            context.status.formation?.modules?.at(index)?.dateDebut?.$status === PropertyStatus.Required &&
            isNullOrUndefined(prop.dateDebut)
          ) {
            result[`formation.modules.${index}.dateDebut`] = SAISIE_OBLIGATOIRE
          }

          if (
            context.status.formation?.modules?.at(index)?.dateFin?.$status === PropertyStatus.Required &&
            isNullOrUndefined(prop.dateFin)
          ) {
            result[`formation.modules.${index}.dateFin`] = SAISIE_OBLIGATOIRE
          }

          if (
            context.status.formation?.modules?.at(index)?.duree?.$status === PropertyStatus.Required &&
            isNullOrUndefined(prop.duree)
          ) {
            result[`formation.modules.${index}.duree`] = SAISIE_OBLIGATOIRE
          }

          if (
            context.status.formation?.modules?.at(index)?.dureeFoad?.$status === PropertyStatus.Required &&
            isNullOrUndefined(prop.dureeFoad)
          ) {
            result[`formation.modules.${index}.dureeFoad`] = SAISIE_OBLIGATOIRE
          }

          if (
            context.status.formation?.modules?.at(index)?.attestationMoyenRealisation?.$status ===
              PropertyStatus.Required &&
            prop.attestationMoyenRealisation !== true
          ) {
            result[`formation.modules.${index}.attestationMoyenRealisation`] = SAISIE_OBLIGATOIRE
          }

          if (
            context.status.formation?.modules?.at(index)?.avecSubrogation?.$status === PropertyStatus.Required &&
            isNullOrUndefined(prop.avecSubrogation)
          ) {
            result[`formation.modules.${index}.avecSubrogation`] = SAISIE_OBLIGATOIRE
          }

          for (const [indexFrais, propFrais] of model.formation!.modules!.at(index)!.fraisAnnexesSalarie!.entries()) {
            if (
              context.status.formation?.modules?.at(index)?.fraisAnnexesSalarie?.at(indexFrais)?.montant?.$status ===
                PropertyStatus.Required &&
              isNullOrUndefined(propFrais.montant)
            ) {
              result[`formation.modules.${index}.fraisAnnexesSalarie.${indexFrais}.montant`] = SAISIE_OBLIGATOIRE
            }

            if (
              context.status.formation?.modules?.at(index)?.fraisAnnexesSalarie?.at(indexFrais)?.quantite?.$status ===
                PropertyStatus.Required &&
              isNullOrUndefined(propFrais.quantite)
            ) {
              result[`formation.modules.${index}.fraisAnnexesSalarie.${indexFrais}.quantite`] = SAISIE_OBLIGATOIRE
            }
          }

          for (const [indexFrais, propFrais] of model.formation!.modules!.at(index)!.fraisPedagogiques!.entries()) {
            if (
              context.status.formation?.modules?.at(index)?.fraisPedagogiques?.at(indexFrais)?.montant?.$status ===
                PropertyStatus.Required &&
              isNullOrUndefined(propFrais.montant)
            ) {
              result[`formation.modules.${index}.fraisPedagogiques.${indexFrais}.montant`] = SAISIE_OBLIGATOIRE
            }

            if (
              context.status.formation?.modules?.at(index)?.fraisPedagogiques?.at(indexFrais)?.quantite?.$status ===
                PropertyStatus.Required &&
              isNullOrUndefined(propFrais.quantite)
            ) {
              result[`formation.modules.${index}.fraisPedagogiques.${indexFrais}.quantite`] = SAISIE_OBLIGATOIRE
            }
          }
        }

        return result
      },
      level: LevelRegleEnum.Transmettre,
    },
    controleEtablissementPrestataireActif: {
      validateur: (model, _gModel, _context) => {
        const result: ModelValidateurResult<ContratProDTO> = {}
        if (isNullOrUndefined(model.formation) || isNullOrUndefined(model.formation?.modules)) return result

        for (const [index, prop] of model.formation!.modules!.entries()) {
          if (
            prop.typeModule === 'Externe' &&
            isNotNullUndefinedOrEmptyString(prop.etablissementPrestataire?.siret?.valeur) &&
            prop.etablissementPrestataire.etatAdministratif === 'F'
          ) {
            result[`formation.modules.${index}.etablissementPrestataire.siret.valeur`] = false
          }
        }

        return result
      },
      message: 'Vous ne pouvez pas sélectionner un établissement inactif, merci de sélectionner un autre établissement',
      level: LevelRegleEnum.Technique,
    },
    controleDureeModule: {
      pre: (model) => {
        return preNotNullOrEmpty(model.formation?.dureeTotale)
      },
      validateur: (model, _gModel, _context) => {
        const result: ModelValidateurResult<ContratProDTO> = {}

        const nbHeureModules = model.formation?.modules?.reduce((sum, module) => sum + (module.duree ?? 0), 0)

        if (nbHeureModules! > model.formation!.dureeTotale!) {
          const message = 'La durée totale des modules est supérieure à la durée totale de formation'
          result[`formation.dureeTotale`] = message
          for (const [index, _prop] of model.formation!.modules!.entries()) {
            result[`formation.modules.${index}.duree`] = message
          }
        }

        if (nbHeureModules! > 0 && nbHeureModules! !== model.formation!.dureeTotale!) {
          result[`formation.dureeTotale`] = "La durée totale des modules n'est pas égale à la durée totale de formation"
        }

        return result
      },
      level: LevelRegleEnum.Metier,
    },
    controleNbModule: {
      validateur: (model, _gModel, _context) => {
        const result: ModelValidateurResult<ContratProDTO> = {}
        if (model.formation?.modules?.length == 0) {
          result[`formation.modules`] = 'Un module est obligatoire'
        }

        return result
      },
      level: LevelRegleEnum.Transmettre,
    },
    controleFichier: {
      validateur: (model, _globalModel, context) => {
        const result: ModelValidateurResult<ContratProDTO> = {}
        if (isAdherentOuMandatGestion(context.typeContext, false)) {
          for (const doc of context.naturesFichiers.obligatoires) {
            if (model.fichiers?.filter((x) => x.nature === doc).length === 0) {
              result[`fichiers`] = false
            }
          }
        }
        return result
      },
      message: SAISIE_OBLIGATOIRE,
      level: LevelRegleEnum.Transmettre,
    },
    controleCodeRncpFormaCodeNsf: {
      validateur: (model, _globalModel, context) => {
        const result: ModelValidateurResult<ContratProDTO> = {}

        if (
          context.status.formation?.rncp?.code?.$status === PropertyStatus.Required &&
          (isNullOrUndefined(model.formation?.rncp) || isNullOrUndefined(model.formation?.rncp?.code))
        )
          result['formation.rncp.code'] = false

        if (
          context.status.formation?.formacode?.code?.$status === PropertyStatus.Required &&
          (isNullOrUndefined(model.formation?.formacode) || isNullOrUndefined(model.formation?.formacode?.code))
        )
          result['formation.formacode.code'] = false

        if (
          context.status.formation?.nsf?.code?.$status === PropertyStatus.Required &&
          (isNullOrUndefined(model.formation?.nsf) || isNullOrUndefined(model.formation?.nsf?.code))
        )
          result['formation.nsf.code'] = false

        return result
      },
      message: SAISIE_OBLIGATOIRE,
      level: LevelRegleEnum.Transmettre,
    },
    controleFinancementObligatoire: {
      validateur: (model, _globalModel, context) => {
        const result: ModelValidateurResult<ContratProDTO> = {}

        if (context.status.financement?.$status === PropertyStatus.Required && isNullOrUndefined(model.financement))
          result['financement'] = false

        return result
      },
      message: SAISIE_OBLIGATOIRE,
      level: LevelRegleEnum.Transmettre,
    },
  },
} as const
